import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { StorefrontProductPicker } from '@/components/features/storefront/StorefrontProductPicker'
import { loadAllProducts, loadPickablePlans, type PickablePlan, type PickableProduct } from '@/components/features/storefront/storefrontProducts'
import type { ModuleFieldComponentProps, StorefrontModuleConfig } from '@/types/space-wizard'

/**
 * What a new store shows (F-29), chosen while creating it. The selection is
 * the wizard's; this component only fetches what there is to pick from, and
 * only once the owner asks to pick. A brand-new store has nothing inactive to
 * keep around, so only active products are listed. F-29 phase 3: one-off
 * products, whole plans and add-ons, picked in three groups.
 */
export const StorefrontModuleFields: React.FC<ModuleFieldComponentProps<StorefrontModuleConfig>> = ({
  config,
  onChange,
  disabled = false
}) => {
  const { t } = useTranslation(['common'])
  const mode = config.mode ?? 'all'
  const picked = config.productIds ?? []
  const pickedPlans = config.planIds ?? []

  const [products, setProducts] = React.useState<PickableProduct[] | null>(null)
  const [plans, setPlans] = React.useState<PickablePlan[]>([])
  const [loading, setLoading] = React.useState(false)
  const [loadFailed, setLoadFailed] = React.useState(false)

  const load = React.useCallback(async () => {
    setLoading(true)
    setLoadFailed(false)
    try {
      const all = await loadAllProducts()
      const active = all.filter((product) => product.status === 'ACTIVE')
      setProducts(active)
      setPlans(await loadPickablePlans(active))
    } catch {
      setLoadFailed(true)
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    if (mode === 'selected' && products === null && !loading && !loadFailed) void load()
  }, [mode, products, loading, loadFailed, load])

  const listOverride =
    mode !== 'selected' ? undefined : loadFailed ? (
      <div className="flex flex-col items-center rounded-lg border border-gray-200 py-6 text-center">
        <AlertTriangle className="mb-2 h-6 w-6 text-amber-500" />
        <p className="text-sm text-gray-700">{t('common:storefront.loadFailed')}</p>
        <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => void load()}>
          <RotateCcw className="mr-2 h-4 w-4" />
          {t('common:retry')}
        </Button>
      </div>
    ) : loading || products === null ? (
      <div className="space-y-2" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-12 animate-pulse rounded-lg bg-gray-100" />
        ))}
      </div>
    ) : undefined

  return (
    <StorefrontProductPicker
      mode={mode}
      onModeChange={(next) => onChange({ ...config, mode: next })}
      picked={picked}
      onPickedChange={(next) => onChange({ ...config, productIds: next })}
      pickedPlans={pickedPlans}
      onPickedPlansChange={(next) => onChange({ ...config, planIds: next })}
      products={products ?? []}
      plans={plans}
      emptyMessage={t('common:storefront.noActiveProducts')}
      listOverride={listOverride}
      disabled={disabled}
    />
  )
}
